import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Lightweight toast system.
//   const { addToast } = useToast();
//   addToast('Saved!', { variant: 'success', duration: 4000 });
// Wrap the app in <ToastProvider>. The region is polite/aria-live for SRs.

const ToastContext = createContext(null);

// Module-level counter for stable ids (Date.now/Math.random avoided elsewhere,
// and a monotonic counter is all we need for React keys).
let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const addToast = useCallback(
    (message, { variant = 'default', duration = 4000 } = {}) => {
      const id = ++nextId;
      setToasts((list) => [...list, { id, message, variant }]);
      if (duration) {
        timers.current[id] = setTimeout(() => removeToast(id), duration);
      }
      return id;
    },
    [removeToast]
  );

  const value = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="ss-toast-region" role="region" aria-live="polite" aria-label="Notifications">
          {toasts.map((t) => (
            <div key={t.id} className={`ss-toast ss-toast--${t.variant}`} role="status">
              <span>{t.message}</span>
              <button className="ss-toast__close" aria-label="Dismiss" onClick={() => removeToast(t.id)}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
